import { useState } from 'react'
import { 
  DocumentArrowDownIcon, 
  XMarkIcon, 
  MagnifyingGlassIcon,
  FunnelIcon,
  CheckCircleIcon,
  ClockIcon,
  ExclamationCircleIcon 
} from '@heroicons/react/24/outline'

/**
 * Página para ver todas las facturas generadas
 * Permite descargar PDFs y cancelar facturas
 */
const TodasFacturas = () => {
  // Estado para las facturas (datos de demostración)
  const [facturas, setFacturas] = useState([
    {
      id: 'INV-001',
      serviceId: 'SERV-12345',
      patientName: 'María García López',
      serviceType: 'Consulta cardiológica general',
      amount: 928.00, // Incluye IVA
      rfc: 'GAML850301ABC',
      email: 'maria.garcia@email.com',
      createdAt: '2024-01-15T10:30:00Z',
      status: 'completed',
      pdfUrl: '#'
    },
    {
      id: 'INV-002',
      serviceId: 'SERV-67890',
      patientName: 'Juan Pérez Martinez',
      serviceType: 'Electrocardiograma de 12 derivaciones',
      amount: 1392.00,
      rfc: 'PEMJ790515XYZ',
      email: 'juan.perez@email.com',
      createdAt: '2024-01-14T14:45:00Z',
      status: 'completed',
      pdfUrl: '#'
    },
    {
      id: 'INV-003',
      serviceId: 'ECOCAR-123',
      patientName: 'Ana Rodríguez Silva',
      serviceType: 'Ecocardiograma transtorácico',
      amount: 2900.00,
      rfc: 'ROSA820920DEF',
      email: 'ana.rodriguez@email.com',
      createdAt: '2024-01-13T09:15:00Z',
      status: 'pending',
      pdfUrl: '#'
    },
    {
      id: 'INV-004',
      serviceId: 'HOLTER-456',
      patientName: 'Carlos López Hernández',
      serviceType: 'Monitoreo Holter de 24 horas',
      amount: 2088.00,
      rfc: 'LOHC751203GHI',
      email: 'carlos.lopez@email.com',
      createdAt: '2024-01-12T16:20:00Z',
      status: 'cancelled',
      pdfUrl: '#'
    }
  ])

  // Estados para filtros y búsqueda
  const [searchTerm, setSearchTerm] = useState('')
  const [statusFilter, setStatusFilter] = useState('all')
  const [dateFilter, setDateFilter] = useState('all')
  const [isLoading, setIsLoading] = useState(false)
  const [notification, setNotification] = useState(null)

  // Filtrar facturas
  const filteredFacturas = facturas.filter(factura => {
    const matchesSearch = 
      factura.patientName.toLowerCase().includes(searchTerm.toLowerCase()) ||
      factura.serviceId.toLowerCase().includes(searchTerm.toLowerCase()) ||
      factura.id.toLowerCase().includes(searchTerm.toLowerCase()) ||
      factura.rfc.toLowerCase().includes(searchTerm.toLowerCase())

    const matchesStatus = statusFilter === 'all' || factura.status === statusFilter

    const matchesDate = (() => {
      if (dateFilter === 'all') return true
      const facturaDate = new Date(factura.createdAt)
      const today = new Date()
      
      switch (dateFilter) {
        case 'today':
          return facturaDate.toDateString() === today.toDateString()
        case 'week':
          const weekAgo = new Date(today.getTime() - 7 * 24 * 60 * 60 * 1000)
          return facturaDate >= weekAgo
        case 'month':
          const monthAgo = new Date(today.getTime() - 30 * 24 * 60 * 60 * 1000)
          return facturaDate >= monthAgo
        default:
          return true
      }
    })()

    return matchesSearch && matchesStatus && matchesDate
  })

  // Mostrar notificación
  const showNotification = (message, type = 'success') => {
    setNotification({ message, type })
    setTimeout(() => setNotification(null), 3000)
  }

  // Descargar PDF
  const handleDownloadPdf = async (facturaId) => {
    setIsLoading(true)
    try {
      // Simular descarga (en implementación real sería una petición al backend)
      await new Promise(resolve => setTimeout(resolve, 1000))
      showNotification(`PDF de la factura ${facturaId} descargado exitosamente`)
    } catch (error) {
      showNotification('Error al descargar el PDF', 'error')
    } finally {
      setIsLoading(false)
    }
  }

  // Cancelar factura
  const handleCancelInvoice = async (facturaId) => {
    if (!window.confirm('¿Estás seguro de que deseas cancelar esta factura?')) {
      return
    }

    setIsLoading(true)
    try {
      // Simular cancelación (en implementación real sería una petición al backend)
      await new Promise(resolve => setTimeout(resolve, 1000))
      
      setFacturas(prev => 
        prev.map(factura => 
          factura.id === facturaId 
            ? { ...factura, status: 'cancelled' }
            : factura
        )
      )
      
      showNotification(`Factura ${facturaId} cancelada exitosamente`)
    } catch (error) {
      showNotification('Error al cancelar la factura', 'error')
    } finally {
      setIsLoading(false)
    }
  }

  // Formatear fecha
  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('es-MX', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    })
  }

  // Obtener ícono y color por estado
  const getStatusConfig = (status) => {
    switch (status) {
      case 'completed':
        return {
          icon: CheckCircleIcon,
          label: 'Completada',
          bgColor: 'bg-green-100',
          textColor: 'text-green-800'
        }
      case 'pending':
        return {
          icon: ClockIcon,
          label: 'Pendiente',
          bgColor: 'bg-yellow-100',
          textColor: 'text-yellow-800'
        }
      case 'cancelled':
        return {
          icon: ExclamationCircleIcon,
          label: 'Cancelada',
          bgColor: 'bg-red-100',
          textColor: 'text-red-800'
        }
      default:
        return {
          icon: ClockIcon,
          label: 'Desconocido',
          bgColor: 'bg-gray-100',
          textColor: 'text-gray-800'
        }
    }
  }

  return (
    <div className="min-h-screen bg-gray-50 py-8 px-4 sm:px-6 lg:px-8">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-8 text-center">
          <h1 className="text-3xl sm:text-4xl font-bold text-primary">Administración de Facturas</h1>
          <p className="mt-2 text-gray-600">Gestiona todas las facturas generadas en el sistema</p>
        </div>

        {/* Notificación */}
        {notification && (
          <div className={`mb-6 p-4 rounded-lg border ${
            notification.type === 'error' 
              ? 'bg-red-50 border-red-200 text-red-700'
              : 'bg-green-50 border-green-200 text-green-700'
          }`}>
            {notification.message}
          </div>
        )}

        {/* Filtros y búsqueda */}
        <div className="bg-white rounded-xl shadow-md p-6 mb-8">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            {/* Búsqueda */}
            <div className="md:col-span-2">
              <label htmlFor="search" className="block text-sm font-medium text-gray-700 mb-1">
                Buscar factura
              </label>
              <div className="relative">
                <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                <input
                  id="search"
                  type="text"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  placeholder="Buscar por nombre, RFC, ID de servicio o factura..."
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary/20 focus:border-primary transition-colors"
                />
              </div>
            </div>

            {/* Filtro por estado */}
            <div>
              <label htmlFor="status" className="block text-sm font-medium text-gray-700 mb-1">
                Estado
              </label>
              <select
                id="status"
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary/20 focus:border-primary transition-colors"
              >
                <option value="all">Todos</option>
                <option value="completed">Completadas</option>
                <option value="pending">Pendientes</option>
                <option value="cancelled">Canceladas</option>
              </select>
            </div>

            {/* Filtro por fecha */}
            <div>
              <label htmlFor="date" className="block text-sm font-medium text-gray-700 mb-1">
                Período
              </label>
              <select
                id="date"
                value={dateFilter}
                onChange={(e) => setDateFilter(e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary/20 focus:border-primary transition-colors"
              >
                <option value="all">Todo el tiempo</option>
                <option value="today">Hoy</option>
                <option value="week">Última semana</option>
                <option value="month">Último mes</option>
              </select>
            </div>
          </div>

          {/* Resumen */}
          <div className="mt-6 pt-4 border-t border-gray-200">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-center">
              <div>
                <p className="text-2xl font-bold text-primary">{filteredFacturas.length}</p>
                <p className="text-sm text-gray-600">Total facturas</p>
              </div>
              <div>
                <p className="text-2xl font-bold text-green-600">
                  {filteredFacturas.filter(f => f.status === 'completed').length}
                </p>
                <p className="text-sm text-gray-600">Completadas</p>
              </div>
              <div>
                <p className="text-2xl font-bold text-yellow-600">
                  {filteredFacturas.filter(f => f.status === 'pending').length}
                </p>
                <p className="text-sm text-gray-600">Pendientes</p>
              </div>
              <div>
                <p className="text-2xl font-bold text-red-600">
                  {filteredFacturas.filter(f => f.status === 'cancelled').length}
                </p>
                <p className="text-sm text-gray-600">Canceladas</p>
              </div>
            </div>
          </div>
        </div>

        {/* Lista de facturas */}
        <div className="bg-white rounded-xl shadow-md overflow-hidden">
          {filteredFacturas.length === 0 ? (
            <div className="p-12 text-center">
              <FunnelIcon className="mx-auto h-12 w-12 text-gray-400 mb-4" />
              <p className="text-gray-500 text-lg">No se encontraron facturas</p>
              <p className="text-gray-400 text-sm">Intenta ajustar los filtros de búsqueda</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Factura / Servicio
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Paciente
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Monto
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Estado
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Fecha
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Acciones
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {filteredFacturas.map((factura) => {
                    const statusConfig = getStatusConfig(factura.status)
                    const StatusIcon = statusConfig.icon

                    return (
                      <tr key={factura.id} className="hover:bg-gray-50 transition-colors">
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div>
                            <p className="text-sm font-medium text-gray-900">{factura.id}</p>
                            <p className="text-sm text-gray-500">{factura.serviceId}</p>
                            <p className="text-xs text-gray-400">{factura.serviceType}</p>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div>
                            <p className="text-sm font-medium text-gray-900">{factura.patientName}</p>
                            <p className="text-sm text-gray-500">{factura.rfc}</p>
                            <p className="text-xs text-gray-400">{factura.email}</p>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <p className="text-sm font-medium text-gray-900">
                            ${factura.amount.toFixed(2)}
                          </p>
                          <p className="text-xs text-gray-500">MXN (con IVA)</p>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${statusConfig.bgColor} ${statusConfig.textColor}`}>
                            <StatusIcon className="h-4 w-4 mr-1" />
                            {statusConfig.label}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {formatDate(factura.createdAt)}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                          <div className="flex flex-col sm:flex-row gap-2">
                            <button
                              onClick={() => handleDownloadPdf(factura.id)}
                              disabled={isLoading}
                              className="inline-flex items-center px-3 py-1 text-xs font-medium text-blue-700 bg-blue-100 rounded-full hover:bg-blue-200 transition-colors disabled:opacity-50"
                            >
                              <DocumentArrowDownIcon className="h-4 w-4 mr-1" />
                              PDF
                            </button>
                            
                            {factura.status !== 'cancelled' && (
                              <button
                                onClick={() => handleCancelInvoice(factura.id)}
                                disabled={isLoading}
                                className="inline-flex items-center px-3 py-1 text-xs font-medium text-red-700 bg-red-100 rounded-full hover:bg-red-200 transition-colors disabled:opacity-50"
                              >
                                <XMarkIcon className="h-4 w-4 mr-1" />
                                Cancelar
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                    )
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Información adicional */}
        <div className="mt-8 bg-blue-50 rounded-xl p-6">
          <h3 className="text-lg font-semibold text-blue-900 mb-2">Información:</h3>
          <ul className="text-blue-800 space-y-1 text-sm">
            <li>• Las facturas completadas han sido procesadas y enviadas al SAT</li>
            <li>• Las facturas pendientes están en proceso de validación</li>
            <li>• Las facturas canceladas no tienen validez fiscal</li>
            <li>• Puedes descargar el PDF de cualquier factura para impresión o archivo</li>
            <li>• La cancelación de facturas debe reportarse al SAT según la normativa vigente</li>
          </ul>
        </div>
      </div>
    </div>
  )
}

export default TodasFacturas
